import { searchLookupOptions } from '../../../api/lookup';
import type { ClarifyFormField } from '../../../types';
import type { FieldValue } from './fields';
import { isPicker } from './fields';
import { isUserPicker, loadUserOptions } from './userOptions';

// Kula FormBuilder's field descriptor. The package ships no usable types
// (its .d.ts points at an unshipped ./src), so we pin the shape we rely on.
export interface KulaFieldConfig {
  name: string;
  type: 'input' | 'select' | 'asyncSelect';
  label: string;
  description?: string;
  placeholder?: string;
  required?: boolean;
  isMulti?: boolean;
  // `description`/`image` are optional per-option extras the Select renders as
  // a subtitle + avatar; backend options only ever carry value/label.
  options?: {
    value: string;
    label: string;
    description?: string;
    image?: string;
  }[];
  fieldProps?: Record<string, unknown>;
}

// Value -> label for everything we've ever seen, so the submit message can
// render a human label for a value picked out of a server search.
export type LabelCache = Map<string, string>;

const seedLabels = (fields: ClarifyFormField[]): LabelCache => {
  const cache: LabelCache = new Map();
  for (const f of fields) {
    for (const o of f.options ?? []) cache.set(o.value, o.label);
  }
  return cache;
};

// A picker starts at its pre-selected option or EMPTY — never options[0].
// Auto-picking the first option silently committed whichever office sorted
// first when the agent's guess had no match in the tenant.
const defaultValue = (field: ClarifyFormField): FieldValue => {
  if (!isPicker(field)) return field.prefill;
  // A restored multi-select answer arrives comma-joined (as the ctx marker
  // stores it); split it back into the chosen values. A single agent guess
  // has no comma, so it stays a one-element array.
  if (field.multi) {
    return field.selected
      ? field.selected
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      : [];
  }
  return field.selected || '';
};

export const buildDefaults = (
  fields: ClarifyFormField[]
): Record<string, FieldValue> =>
  Object.fromEntries(fields.map((f) => [f.key, defaultValue(f)]));

const promptFor = (field: ClarifyFormField, verb: string): string =>
  `${verb} ${field.label.toLowerCase().replace(/\?$/, '')}`;

export const buildFormSchema = (
  fields: ClarifyFormField[],
  labels: LabelCache
): KulaFieldConfig[] =>
  fields.map((field) => {
    if (!isPicker(field)) {
      return {
        name: field.key,
        type: 'input',
        label: field.label,
        description: field.help,
        // Backend-authored example text — renders grey, never submits; a
        // required empty field blocks submit (FormBuilder validation).
        placeholder: field.placeholder,
        required: field.required === true,
      };
    }

    const base: KulaFieldConfig = {
      name: field.key,
      type: field.search ? 'asyncSelect' : 'select',
      label: field.label,
      description: field.help,
      // Single pickers must be answered — an empty value reaches the agent as
      // "field=" and creates the record with that field unset.
      required: !field.multi,
      isMulti: field.multi,
      options: field.options ?? [],
      placeholder: promptFor(field, field.search ? 'Search' : 'Select'),
    };

    // Cache labels off a fetched page so the submit message can show names
    // rather than raw values (`user:4466`).
    const remember = (opts: { value: string; label: string }[]) => {
      for (const o of opts) labels.set(o.value, o.label);
      return opts;
    };

    // Select debounces the keystrokes (300ms) and drops superseded results, so
    // loadOptions only has to fetch. `defaultOptions: true` makes the menu
    // auto-fetch (empty query) on first open — without it react-select never
    // calls loadOptions at all and the picker just reads "No options".
    const asyncField = (
      fetchPage: (_input: string) => Promise<{ value: string; label: string }[]>
    ): KulaFieldConfig => ({
      ...base,
      type: 'asyncSelect',
      fieldProps: {
        selectType: 'async',
        defaultOptions: true,
        loadOptions: async (input: string) => remember(await fetchPage(input)),
      },
    });

    // People picker: the agent's user lookup yields nothing here, so search the
    // users directly instead.
    if (isUserPicker(field)) {
      const prefix = field.search?.valuePrefix;
      return asyncField((input) => loadUserOptions(input, prefix));
    }

    if (!field.search) return base;

    const search = field.search;
    return asyncField((input) => searchLookupOptions(search, input));
  });

export { seedLabels };
